'use client';

import { activateCard } from '@/app/actions/membership';
import type { CardInfo } from '@/app/actions/user';
import { VALIDITY_PERIOD_LABEL } from '@/app/(public)/pricing/plan-utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatPrice } from '@/lib/util';
import { CheckIcon, CreditCardIcon, Loader2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

interface ActivateCardModalProps {
  cards: CardInfo[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  defaultOrderId?: string;
}

const ActivateCardModal = ({
  cards,
  isOpen,
  onOpenChange,
  onSuccess,
  defaultOrderId,
}: ActivateCardModalProps) => {
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(
    null,
  );
  const [loading, setLoading] = useState(false);

  const queuedCards = cards.filter((c) => c.card_status === 'QUEUED');

  // 弹窗打开时，使用 defaultOrderId 或自动选择第一张
  useEffect(() => {
    if (isOpen) {
      if (defaultOrderId && queuedCards.some((c) => c.order_id === defaultOrderId)) {
        setSelectedOrderId(defaultOrderId);
      } else if (queuedCards.length === 1) {
        setSelectedOrderId(queuedCards[0].order_id);
      } else {
        setSelectedOrderId(null);
      }
    }
  }, [isOpen, defaultOrderId]);

  const handleActivate = async () => {
    if (!selectedOrderId) return;
    setLoading(true);
    try {
      await activateCard(selectedOrderId);
      toast.success('会员卡激活成功');
      onOpenChange(false);
      onSuccess();
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : '激活失败，请稍后重试',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCardIcon className="w-5 h-5" />
            激活会员卡
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          选择一张会员卡进行激活，激活后立即生效。
        </p>

        {queuedCards.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            暂无可激活的卡片
          </p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {queuedCards.map((card) => (
              <button
                key={card.order_id}
                type="button"
                onClick={() => setSelectedOrderId(card.order_id)}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${
                  selectedOrderId === card.order_id
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-sm">
                      {card.product_info.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                      <span>
                        {VALIDITY_PERIOD_LABEL[
                          card.product_info.validity_period
                        ] ?? card.product_info.validity_period}
                      </span>
                      <span>·</span>
                      <span>{formatPrice(card.amount)}</span>
                    </div>
                  </div>
                  {selectedOrderId === card.order_id && (
                    <CheckIcon className="w-4 h-4 text-primary shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        <Button
          onClick={handleActivate}
          disabled={!selectedOrderId || loading}
          className="w-full"
        >
          {loading && <Loader2Icon className="w-4 h-4 animate-spin mr-2" />}
          确认激活
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default ActivateCardModal;
